/**
 * PerformanceCharts - Enterprise Performance Visualization
 * Grafici real-time delle performance del sistema
 */

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useMetrics } from "@/hooks/useMetrics";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { TrendingUp, TrendingDown, Activity, Clock, Zap } from "lucide-react";

interface ChartDataPoint {
    time: string;
    timestamp: number;
    cpu: number;
    memory: number;
    network: number;
    players: number;
}

export const PerformanceCharts: React.FC = () => {
    const { metrics, getGamingMetrics } = useMetrics();
    const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
    const [timeRange, setTimeRange] = useState<"1h" | "6h" | "24h">("1h");

    const gamingMetrics = getGamingMetrics();

    // Update chart data from latest metrics
    useEffect(() => {
        const updateChartData = () => {
            if (!metrics?.system) return;

            const now = Date.now();
            const newDataPoint: ChartDataPoint = {
                time: new Date().toLocaleTimeString(),
                timestamp: now,
                cpu: metrics.system.cpu_percent || 0,
                memory: metrics.system.memory_percent || 0,
                network: metrics.system.network_io
                    ? (metrics.system.network_io.bytes_sent +
                          metrics.system.network_io.bytes_recv) /
                      1024 /
                      1024
                    : 0,
                players: gamingMetrics?.total_players || 0,
            };

            setChartData((prevData) => {
                const maxPoints =
                    timeRange === "1h" ? 60 : timeRange === "6h" ? 72 : 144;
                const cutoffTime =
                    now -
                    (timeRange === "1h"
                        ? 3600000
                        : timeRange === "6h"
                          ? 21600000
                          : 86400000);

                const filteredData = prevData.filter(
                    (point) => point.timestamp > cutoffTime,
                );
                const updatedData = [...filteredData, newDataPoint];

                return updatedData.slice(-maxPoints);
            });
        };

        // Initial data point
        updateChartData();

        // Update every 30 seconds
        const interval = setInterval(updateChartData, 30000);
        return () => clearInterval(interval);
    }, [metrics, timeRange, gamingMetrics]);

    const getHealthTrend = () => {
        if (chartData.length < 2) return null;

        const recent = chartData.slice(-5);
        const avgRecent =
            recent.reduce((sum, point) => sum + point.cpu + point.memory, 0) /
            (recent.length * 2);
        const previous = chartData.slice(-10, -5);
        const avgPrevious =
            previous.length > 0
                ? previous.reduce(
                      (sum, point) => sum + point.cpu + point.memory,
                      0,
                  ) /
                  (previous.length * 2)
                : avgRecent;

        const trend = avgRecent - avgPrevious;
        return {
            direction: trend > 5 ? "up" : trend < -5 ? "down" : "stable",
            value: Math.abs(trend).toFixed(1),
        };
    };

    const healthTrend = getHealthTrend();

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* System Performance Chart */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
            >
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <Activity className="h-4 w-4" />
                                Performance sistema
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                {healthTrend && (
                                    <Badge
                                        variant={
                                            healthTrend.direction === "down"
                                                ? "default"
                                                : "secondary"
                                        }
                                        className="text-xs"
                                    >
                                        {healthTrend.direction === "up" ? (
                                            <TrendingUp className="h-3 w-3 mr-1" />
                                        ) : healthTrend.direction === "down" ? (
                                            <TrendingDown className="h-3 w-3 mr-1" />
                                        ) : (
                                            <Activity className="h-3 w-3 mr-1" />
                                        )}
                                        {healthTrend.direction}
                                    </Badge>
                                )}
                                <div className="flex rounded-md border">
                                    {(["1h", "6h", "24h"] as const).map(
                                        (range) => (
                                            <Button
                                                key={range}
                                                size="sm"
                                                variant={
                                                    timeRange === range
                                                        ? "default"
                                                        : "ghost"
                                                }
                                                className="h-6 px-2 text-xs"
                                                onClick={() =>
                                                    setTimeRange(range)
                                                }
                                            >
                                                {range}
                                            </Button>
                                        ),
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        className="opacity-30"
                                    />
                                    <XAxis
                                        dataKey="time"
                                        fontSize={10}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        fontSize={10}
                                        tickLine={false}
                                        axisLine={false}
                                        domain={[0, 100]}
                                    />
                                            <Tooltip
                                        contentStyle={{
                                            backgroundColor:
                                                "rgba(255, 255, 255, 0.95)",
                                            border: "1px solid #e2e8f0",
                                            borderRadius: "6px",
                                            fontSize: "12px",
                                        }}
                                        formatter={(
                                            value: number,
                                            name: string,
                                        ) => [
                                            `${value.toFixed(1)}%`,
                                            name === "cpu" ? "CPU" : "Memoria",
                                        ]}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="cpu"
                                        stroke="#3b82f6"
                                        strokeWidth={2}
                                        dot={false}
                                        name="cpu"
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="memory"
                                        stroke="#10b981"
                                        strokeWidth={2}
                                        dot={false}
                                        name="memory"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                    <span>Uso CPU</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <span>Uso memoria</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                <span>Ultime {timeRange}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Gaming Activity Chart */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
            >
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <Zap className="h-4 w-4" />
                            Attivita gaming
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData}>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        className="opacity-30"
                                    />
                                    <XAxis
                                        dataKey="time"
                                        fontSize={10}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        fontSize={10}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor:
                                                "rgba(255, 255, 255, 0.95)",
                                            border: "1px solid #e2e8f0",
                                            borderRadius: "6px",
                                            fontSize: "12px",
                                        }}
                                        formatter={(value: number) => [
                                            `${value}`,
                                            "Giocatori",
                                        ]}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="players"
                                        stroke="#8b5cf6"
                                        fill="#8b5cf6"
                                        fillOpacity={0.3}
                                        strokeWidth={2}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                            <div>
                                <div className="text-lg font-semibold">
                                    {gamingMetrics?.total_players || 0}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Giocatori attuali
                                </div>
                            </div>
                            <div>
                                <div className="text-lg font-semibold">
                                    {gamingMetrics?.online_servers || 0}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Server online
                                </div>
                            </div>
                            <div>
                                <div className="text-lg font-semibold">
                                    {(() => {
                                        const total = Number(
                                            gamingMetrics?.total_servers || 0,
                                        );
                                        const online = Number(
                                            gamingMetrics?.online_servers || 0,
                                        );
                                        if (!total) return "0.0%";
                                        return `${((online / total) * 100).toFixed(1)}%`;
                                    })()}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Disponibilita
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};
